import type { ElementDoc, ElementPartDoc } from "@mountayaapp/markdoc";

import { ELEMENT_DOCS, ELEMENT_PAGES } from "../elements.gen";
import { AUTHORED_PAGE_TABS } from "./navigation";

/**
 * @mountayaapp/docs page-level tabs. A guide that documents elements carries its
 * generated tables on tabs beside the prose rather than interleaved with it, the way
 * `mantine.dev` splits Documentation / Props / Styles API.
 *
 * **Derived, not authored.** No twin file is written for a tab: the set of tabs a page
 * has, and the body of each one, are computed from `ELEMENT_PAGES` and `ELEMENT_DOCS`,
 * which the element manifests already produce and gate. A new element joins its page's
 * Props tab on the day it is documented, because the manifest is the thing that says
 * which page documents it.
 *
 * The one exception is declared, not inferred: {@link AUTHORED_PAGE_TABS} names the tabs
 * that are a pairing of two authored pages, which no manifest can know about.
 */

/**
 * PageTab is one tab in a page's strip: its `id` (stable, used for the active mark and
 * as a test handle), the localized `labelKey`, and the `slug` it is served at.
 */
export interface PageTab {
  readonly id: string;
  readonly labelKey: string;
  readonly slug: string;
}

/** The two tabs derived from the element catalogue, as the suffix each is served under. */
export type DerivedTabId = "props" | "styling";

const DERIVED_TAB_IDS: readonly DerivedTabId[] = ["props", "styling"];

/**
 * The page a reader arrives on, which is the guide itself. It is always present and
 * always first: a strip whose first entry was a generated table would put the reference
 * ahead of the explanation, which is the ordering this whole change is undoing.
 */
const DOCUMENTATION_TAB_ID = "documentation";

/**
 * CUSTOMIZE_SLUG is where the shared contract is explained in full.
 *
 * The Styling tab links there rather than restating it. Icons, strings, `on*` handlers and
 * the className/style pair are one contract every element honours, and a copy of it per
 * element is a copy per element to keep true.
 */
const CUSTOMIZE_SLUG = "sdks/typescript/customize";

/** parts is an element's compound parts, which carry props of their own. */
const parts = (name: string): readonly ElementPartDoc[] => ELEMENT_DOCS[name]?.parts ?? [];

/** ownProps counts the props that belong to an element or part rather than to a seam. */
const ownProps = (props: readonly { readonly seam?: string }[]): number =>
  props.filter((prop) => prop.seam === undefined).length;

/** seamProps counts the props of one seam on an element or part. */
const seamProps = (props: readonly { readonly seam?: string }[], seam: string): number =>
  props.filter((prop) => prop.seam === seam).length;

/**
 * stylable answers whether an element or part exposes a styling surface at all: a
 * `className`/`style` seam, or a DOM intrinsic whose attributes it spreads.
 *
 * It is the whole reason the third tab is conditional. Six of the twenty element pages
 * expose none, and a Styling tab over an empty table is a tab that says an element can be
 * styled when it cannot.
 */
const stylable = (entry: ElementDoc | ElementPartDoc): boolean =>
  entry.domAttributes !== undefined || seamProps(entry.props, "style") > 0;

/** elementsOn is the elements a guide slug documents, in the manifest's reading order. */
const elementsOn = (slug: string): readonly string[] => ELEMENT_PAGES[slug] ?? [];

/**
 * hasDerivedTab answers whether a guide slug carries one of the two derived tabs: Props
 * when it documents any element, Styling when at least one of those elements or their
 * parts exposes a styling surface.
 */
const hasDerivedTab = (slug: string, tab: DerivedTabId): boolean => {
  const names = elementsOn(slug);
  if (names.length === 0) {
    return false;
  }

  if (tab === "props") {
    return true;
  }

  return names.some((name) => {
    const element = ELEMENT_DOCS[name];

    return element !== undefined && (stylable(element) || element.parts.some(stylable));
  });
};

/**
 * derivedTabSlug is where a guide's derived tab is served: the guide's own slug with the
 * tab appended. A tab is a path under the page it belongs to, so a reader can see from
 * the URL alone which guide they are reading a table of.
 */
export const derivedTabSlug = (guideSlug: string, tab: DerivedTabId): string =>
  `${guideSlug}/${tab}`;

/**
 * DerivedTab names one synthesized page: the guide it hangs off and which tab it is.
 * The registry builds the page from this pair.
 */
export interface DerivedTab {
  readonly guideSlug: string;
  readonly tab: DerivedTabId;
}

/**
 * resolveDerivedTab maps a slug back to the guide and tab it is the synthesized page of,
 * or `null` when the slug is not one.
 *
 * Deliberately strict: the stem has to be a slug the element manifests actually assign
 * elements to. A looser rule would answer for `<anything>/props`, which is a page the
 * build would then emit for a guide that does not exist.
 */
export const resolveDerivedTab = (slug: string): DerivedTab | null => {
  for (const tab of DERIVED_TAB_IDS) {
    const suffix = `/${tab}`;
    if (!slug.endsWith(suffix)) {
      continue;
    }

    const guideSlug = slug.slice(0, -suffix.length);
    if (hasDerivedTab(guideSlug, tab)) {
      return { guideSlug, tab };
    }
  }

  return null;
};

/**
 * listDerivedTabs is every synthesized tab page across the whole corpus, in the
 * catalogue's own slug order. The prerender walks it to emit one static file per tab;
 * nothing else needs the whole set, which is why resolution goes the other way round for
 * a reader asking for one page.
 */
export const listDerivedTabs = (): DerivedTab[] =>
  Object.keys(ELEMENT_PAGES).flatMap((guideSlug) =>
    DERIVED_TAB_IDS.filter((tab) => hasDerivedTab(guideSlug, tab)).map((tab) => ({
      guideSlug,
      tab,
    })),
  );

/**
 * resolveGuideSlug maps any slug in a tab set — a guide, one of its derived tabs, or one
 * of its authored ones — to the guide the set hangs off. It is what lets the strip render
 * identically whichever of its tabs a reader is standing on.
 */
const resolveGuideSlug = (slug: string): string => {
  const derived = resolveDerivedTab(slug);
  if (derived !== null) {
    return derived.guideSlug;
  }

  for (const [guideSlug, tabs] of Object.entries(AUTHORED_PAGE_TABS)) {
    if (tabs.some((tab) => tab.slug === slug)) {
      return guideSlug;
    }
  }

  return slug;
};

/**
 * listPageTabs returns the tab set a slug belongs to: the guide first, then its authored
 * tabs, then the derived ones. It answers the same list for every slug in the set, so a
 * reader on the Props tab sees the strip they arrived through.
 *
 * A page with no tabs beyond itself gets a single-entry list, which is the signal
 * `DocsPageTabs` renders nothing on. That is every non-element page in the corpus and six
 * of the twenty element pages, so the strip is the exception rather than the frame.
 */
export const listPageTabs = (slug: string): readonly PageTab[] => {
  const guideSlug = resolveGuideSlug(slug);
  const tabs: PageTab[] = [
    { id: DOCUMENTATION_TAB_ID, labelKey: "page.tab.documentation", slug: guideSlug },
  ];

  for (const tab of AUTHORED_PAGE_TABS[guideSlug] ?? []) {
    tabs.push({ id: tab.slug, labelKey: tab.labelKey, slug: tab.slug });
  }

  for (const tab of DERIVED_TAB_IDS) {
    if (hasDerivedTab(guideSlug, tab)) {
      tabs.push({
        id: tab,
        labelKey: `page.tab.${tab}`,
        slug: derivedTabSlug(guideSlug, tab),
      });
    }
  }

  return tabs;
};

/**
 * isPageTabSlug answers whether a slug is reached through a page's tab strip rather than
 * through the sidebar. The registry's orphan warning asks, because a tab page is filed in
 * no navigation group on purpose and would otherwise report as unreachable.
 */
export const isPageTabSlug = (slug: string): boolean =>
  resolveDerivedTab(slug) !== null ||
  Object.values(AUTHORED_PAGE_TABS).some((tabs) => tabs.some((tab) => tab.slug === slug));

/**
 * yamlString quotes a value for the synthesized frontmatter. Double-quoted YAML takes
 * JSON's own escaping, so a title carrying a colon or a quote survives rather than
 * dropping the page — an unquoted `: ` in a description is the one authoring mistake in
 * this corpus that loses a whole page silently.
 */
const yamlString = (value: string): string => JSON.stringify(value);

/**
 * propsTable is one generated table, as the authored tag that produces it.
 *
 * The tag, rather than a renderer of this module's own: it transforms into the same
 * Properties/Property tags a hand-written table uses, so the rows reach the table of
 * contents, the search index and the page's clean-Markdown twin exactly as authored ones
 * do — and a reader cannot tell which tables were generated, which is the point.
 */
const propsTable = (reference: string, filter: string): string =>
  `{% props of=${yamlString(reference)} ${filter} /%}`;

/**
 * buildPropsBody renders the Props tab: one section per element, each part under it, and
 * the handler seams gathered at the end.
 *
 * Handlers close the tab under a heading of their own because they are behaviour rather
 * than styling — an `on*` prop suppresses the element's own default — so they belong
 * beside the props a consumer sets, not beside the class names.
 */
const buildPropsBody = (names: readonly string[]): string[] => {
  const blocks: string[] = [];

  for (const name of names) {
    const element = ELEMENT_DOCS[name];
    if (element === undefined) {
      continue;
    }

    const sections: string[] = [];
    if (ownProps(element.props) > 0 || element.domAttributes !== undefined) {
      sections.push(propsTable(name, 'omit="seams"'));
    }

    for (const part of parts(name)) {
      if (ownProps(part.props) === 0 && part.domAttributes === undefined) {
        continue;
      }

      sections.push(
        `### ${name}.${part.name}`,
        part.summary,
        propsTable(`${name}.${part.name}`, 'omit="seams"'),
      );
    }

    if (sections.length > 0) {
      blocks.push(`## ${name}`, ...sections);
    }
  }

  const withHandlers = names.filter((name) => {
    const element = ELEMENT_DOCS[name];

    return element !== undefined && seamProps(element.props, "handler") > 0;
  });

  if (withHandlers.length > 0) {
    blocks.push(
      "## Handlers",
      "An `on*` handler suppresses the element's own default. Left unset, the element acts; set, it becomes presentational and your handler decides. The two never both fire.",
    );

    for (const name of withHandlers) {
      // One element is its own heading already — the h1 names it — so a second heading
      // repeating the name would be a table of contents entry that says nothing.
      if (withHandlers.length > 1) {
        blocks.push(`### ${name}`);
      }

      blocks.push(propsTable(name, 'only="handlers"'));
    }
  }

  return blocks;
};

/**
 * buildStylingBody renders the Styling tab: the className/style seam for every element
 * and part that exposes one, plus the DOM intrinsic an element spreads the attributes of.
 *
 * An element that exposes neither is omitted rather than shown with an empty table, and a
 * page where that is true of every element has no Styling tab at all.
 */
const buildStylingBody = (names: readonly string[]): string[] => {
  const blocks: string[] = [];

  for (const name of names) {
    const element = ELEMENT_DOCS[name];
    if (element === undefined) {
      continue;
    }

    const sections: string[] = [];
    if (stylable(element)) {
      sections.push(propsTable(name, 'only="style"'));
    }

    for (const part of parts(name)) {
      if (!stylable(part)) {
        continue;
      }

      sections.push(`### ${name}.${part.name}`, propsTable(`${name}.${part.name}`, 'only="style"'));
    }

    if (sections.length > 0) {
      blocks.push(`## ${name}`, ...sections);
    }
  }

  return blocks;
};

/**
 * buildTabSource is the synthetic Markdoc source for one derived tab page: frontmatter,
 * the page's own heading, a line pointing back at the guide, and the generated tables.
 *
 * The strings are English literals rather than locale keys for the same reason the
 * frontmatter of every authored page is: this is page CONTENT, and the corpus is English
 * only. The chrome around it — the tab labels themselves — is localized.
 */
export const buildTabSource = (
  { guideSlug, tab }: DerivedTab,
  guideTitle: string,
  guideHref: string,
): string => {
  const names = elementsOn(guideSlug);
  const isProps = tab === "props";
  const title = `${guideTitle} ${isProps ? "props" : "styling"}`;
  const description = isProps
    ? `Every prop the elements documented on ${guideTitle} accept, with their types, their defaults and the handlers that take over their behaviour.`
    : `The className and style seam of every element documented on ${guideTitle}, and the DOM attributes each one spreads.`;

  const lead = isProps
    ? `The generated props of every element on [${guideTitle}](${guideHref}). The guide is where they are explained; this is the whole surface.`
    : `How the elements on [${guideTitle}](${guideHref}) are painted. Every element takes \`className\` and \`style\` on the same terms, alongside the icons and strings that [customizing the elements](/en/${CUSTOMIZE_SLUG}/) explains in full.`;

  const body = isProps ? buildPropsBody(names) : buildStylingBody(names);

  return [
    "---",
    `title: ${yamlString(title)}`,
    `description: ${yamlString(description)}`,
    "toc: true",
    "---",
    "",
    `# ${title}`,
    "",
    lead,
    "",
    body.join("\n\n"),
    "",
  ].join("\n");
};
